import React, { useContext, useEffect, useState } from "react";
import {
    Box,
    Button,
    Card,
    Divider,
    Grid,
    IconButton,
    Typography,
} from "@mui/material";

import { styled, useTheme } from "@mui/material/styles";
import { tokens } from "../../../../theme";
import useApi from "../../../shared/hooks/useApi";
import FullScreenGraphsDialog from "./FullScreenGraphsDialog";
import dayjs from "dayjs";
import { CustomInfoButton } from "../../components/infoButtons/CustomInfoButton";
import EboilerGraph from "./graphs/EboilerGraph";
import W95Graph from "./graphs/W95Graph";
import W55Graph from "./graphs/W55Graph";
import W7Graph from "./graphs/W7Graph";
import { useSettings } from '../../context/SettingsContext';
import { CustomManualInfoButton } from "../../components/infoButtons/CustomManualInfoButton";
import { CustomInfoButtonAfrr } from "../../components/infoButtons/CustomInfoButtonAfrr";

const Dashboard = ({ setLoading }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { apiPost, apiGet, loadingApi, errorPosting } = useApi();
    const [fullscreenGraphs, setFullscreenGraphs] = useState(false);
    const [screenSize, setScreenSize] = useState({ width: window.innerWidth, height: window.innerHeight });
    const [graphSize, setGraphSize] = useState({ width: (window.innerWidth - 200) / 2, height: (window.innerHeight - 100) / 2 });
    const [bufferValues, setBufferValues] = useState([]);
    const { settingsPairs } = useSettings();
    const [currentTime, setCurrentTime] = useState(dayjs().format("DD-MM-YYYY HH:mm:ss"));
    const [afrrReservations, setAfrrReservations] = useState([]);
    const [dateFrom, setDateFrom] = useState(dayjs().subtract(1, 'day'));
    const [dateTo, setDateTo] = useState(dayjs().add(1, 'day'));

    useEffect(() => {
        let fromDate = dayjs().set("hour", 0);
        fromDate = fromDate.toISOString();
        const getDataRoute = `/esid-24-003/get-afrr-reservations-by-date?from=${dateFrom.format('YYYY-MM-DD')}&to=${dateTo.format('YYYY-MM-DD')}`;
        try {
            const getAfrrReservations = async () => {
                const data = await apiGet("UX_HTTP_API", getDataRoute);
                if (!data) {
                    console.error("API returned undefined data");
                    return;
                }
                const allReservations = data.flatMap(item => item.afrr_reservations || []);
                const combinedData = {
                    afrr_reservations: allReservations
                };
                //setAfrrReservations(getReservationsAfterCurrentTime(combinedData.afrr_reservations));
                setAfrrReservations(combinedData.afrr_reservations);
            };
            getAfrrReservations();

        } catch (error) {
            console.error('Error fetching data:', error);
        }
    }, []);
    
    
    useEffect(() => {
        const handleResize = () => {
            setScreenSize({ width: window.innerWidth, height: window.innerHeight - 50 });
            setGraphSize({ width: (window.innerWidth - 100) / 2, height: (window.innerHeight - 100) / 2 });
        };
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [window.innerWidth]);
    /* 'W95-TAN1-FILL.SP',  // SP : Setpoint
            'W95-TAN1-FILL.PV',  // PV : Process Value */
    const selectedOptions = [
        'BELERE_UTI.W95-BO1-CV01_FLEX.SP',  // SP : Setpoint aFRR
        'W95-BO1-EY001',  // PV : Process Value aFRR
        'W95-TAN1-FILL.SP',  // SP : Setpoint 
        'W95-TAN1-FILL.PV',  // PV : Process Value 
        'BELERE_UTI.W95-TAN1-FILL_FLEX.SP', // ES : Endustries Setpoint
        'W55-TAN1-FILL.SP',  // SP : Setpoint
        'W55-TAN1-FILL.PV',  // PV : Process Value
        'BELERE_UTI.W55-TAN1-FILL_FLEX.SP', // ES : Endustries Setpoint
        'W7-TAN1-FILL.SP',  // SP : Setpoint
        'W7-TAN1-FILL.PV',  // PV : Process Value
        'BELERE_UTI.W7-TAN1-FILL_FLEX.SP', // ES : Endustries Setpoint
    ];
    const returnKeyToTitle = (key) => {
        if (key.includes("W95-TAN1-FILL.SP")) {
            return { title: "W95-TAN1-FILL.SP", type: "sp" };
        } else if (key.includes("W95-TAN1-FILL.PV")) {
            return { title: "W95-TAN1-FILL.PV", type: "pv" };
        } else if (key.includes("BELERE_UTI.W95-TAN1-FILL_FLEX.SP")) {
            return { title: "W95-TAN1-FILL_FLEX.SP", type: "es" };
        } else if (key.includes("W55-TAN1-FILL.SP")) {
            return { title: "W55-TAN1-FILL.SP", type: "sp" };
        } else if (key.includes("W55-TAN1-FILL.PV")) {
            return { title: "W55-TAN1-FILL.PV", type: "pv" };
        } else if (key.includes("BELERE_UTI.W55-TAN1-FILL_FLEX.SP")) {
            return { title: "W55-TAN1-FILL_FLEX.SP", type: "es" };
        } else if (key.includes("W7-TAN1-FILL.SP")) {
            return { title: "W7-TAN1-FILL.SP", type: "sp" };
        } else if (key.includes("W7-TAN1-FILL.PV")) {
            return { title: "W7-TAN1-FILL.PV", type: "pv" };
        } else if (key.includes("BELERE_UTI.W7-TAN1-FILL_FLEX.SP")) {
            return { title: "W7-TAN1-FILL_FLEX.SP", type: "es" };
        } else if (key.includes("BELERE_UTI.W95-BO1-CV01_FLEX.SP")) {
            return { title: "W95-BO1-CV01_FLEX.SP", type: "sp" };
        } else if (key.includes("W95-BO1-EY001")) {
            return { title: "W95-BO1-EY001", type: "pv" };
        }
        return key;
    }
    const findLastValueBeforeCurrentHour = (series) => {
        const currentHour = dayjs().hour();
        let pivotValue = 0;
        let spValue = 0;
        let pvValue = 0;
        let esValue = 0;
        let pivotTitle = "";
        let pivotArray = [];
        series.forEach(element => {
            pivotTitle = element.name;
            let lasttime = 0;
            let lastValue = null;
            // Sort data by timestamp to ensure we get the latest value
            element.data.sort((a, b) => a.x - b.x);

            element.data.forEach(data => {
                const dataHour = dayjs(data.x).hour();
                if (dataHour <= currentHour) {
                    if (pivotTitle === "BELERE_UTI.W95-BO1-CV01_FLEX.SP" || pivotTitle === "W55-TAN1-FILL.SP" || pivotTitle === "W7-TAN1-FILL.SP" || pivotTitle === "W95-TAN1-FILL.SP") {
                        spValue = data.y;
                        /* if(pivotTitle === "BELERE_UTI.W95-BO1-CV01_FLEX.SP"){
                            console.log("spValue", spValue,"date:", dayjs(data.x).format('YYYY-MM-DD HH:mm:ss'));
                        } */
                    } else if (pivotTitle === "W95-BO1-EY001" || pivotTitle === "W55-TAN1-FILL.PV" || pivotTitle === "W7-TAN1-FILL.PV" || pivotTitle === "W95-TAN1-FILL.PV") {
                        pvValue = data.y;
                        /* if(pivotTitle === "W95-BO1-EY001"){
                            console.log("pvValue:",pvValue,"date:", dayjs(data.x).format('YYYY-MM-DD HH:mm:ss'));
                        } */
                    } else if (pivotTitle === "BELERE_UTI.W95-TAN1-FILL_FLEX.SP" || pivotTitle === "BELERE_UTI.W55-TAN1-FILL_FLEX.SP" || pivotTitle === "BELERE_UTI.W7-TAN1-FILL_FLEX.SP") {
                        esValue = data.y;
                        /* if(pivotTitle === "BELERE_UTI.W95-TAN1-FILL_FLEX.SP"){
                            console.log("esValue:",esValue,"date:", dayjs(data.x).format('YYYY-MM-DD HH:mm:ss'));
                        } */
                    }
                    pivotValue = data.y;
                    lasttime = data.x;
                    lastValue = data.y;
                }
            });
            if (returnKeyToTitle(pivotTitle).type == "sp") {

                let value = {
                    title: returnKeyToTitle(pivotTitle).title,
                    spValue: spValue,
                    date: dayjs(lasttime).format('YYYY-MM-DD HH:mm:ss')
                }
                pivotArray.push(value);
            } else if (returnKeyToTitle(pivotTitle).type == "pv") {
                let value = {
                    title: returnKeyToTitle(pivotTitle).title,
                    pvValue: pvValue,
                    date: dayjs(lasttime).format('YYYY-MM-DD HH:mm:ss')
                }
                pivotArray.push(value);
            } else if (returnKeyToTitle(pivotTitle).type == "es") {
                let value = {
                    title: returnKeyToTitle(pivotTitle).title,
                    esValue: esValue,
                    date: dayjs(lasttime).format('YYYY-MM-DD HH:mm:ss')
                }
                pivotArray.push(value);
            }

        });
        //create me this object from pivotArray group W7 W55 W95 get all spValue, pvValue, esValue
        const transformedData = pivotArray.reduce((acc, item) => {
            // Determine which group (W95, W55, W7) this item belongs to
            let group = null;
            if (item.title.includes("W95-TAN1-FILL")) group = "W95";
            else if (item.title.includes("W55")) group = "W55";
            else if (item.title.includes("W7")) group = "W7";
            else if (item.title.includes("W95-BO1-")) group = "aFRR";
            if (!group) return acc;

            // Initialize the group if it doesn't exist
            if (!acc[group]) {
                acc[group] = {
                    title: group,
                    spValue: undefined,
                    pvValue: undefined,
                    esValue: undefined,
                    date: item.date
                };
            }

            // Assign the value to the correct key
            if ("spValue" in item) acc[group].spValue = item.spValue;
            if ("pvValue" in item) acc[group].pvValue = item.pvValue;
            if ("esValue" in item) acc[group].esValue = item.esValue;

            // Optionally, update the date if you want the latest one
            acc[group].date = item.date;

            return acc;
        }, {});
        if (Object.keys(transformedData).length > 0) {
            setBufferValues(transformedData);
        }
    }


    useEffect(() => {
        const getData = async () => {
            let fromDate = dayjs().set("hour", 0);
            // start from 20th of the mon   th
            fromDate = fromDate.toISOString();  // fromDate is your Dayjs object
            let toDate = dayjs().set("hour", 24);
            toDate = toDate.toISOString();
            const getDataRoute = `/esid-24-003/get-data?from=${fromDate}&to=${toDate}&columns=${encodeURIComponent(selectedOptions.join(","))}`;
            const data = await apiGet("UX_HTTP_API", getDataRoute);
            if (data) {

                const series = Object.entries(data).map(([key, dataPoints]) => ({
                    name: key,
                    data: dataPoints.map(point => ({
                        x: new Date(point.datetime).getTime(),
                        y: Math.floor(point.value)
                    }))
                }));
                const lastValueBeforeCurrentHour = findLastValueBeforeCurrentHour(series);
            }
        };
        getData();
        const interval = setInterval(() => {
            getData();
        }, 30000);
        return () => clearInterval(interval);
    }, []);

    const [state, setState] = useState([]);


    const getChartConfigs = (responseCopy) => {
        let response = { ...responseCopy };
        //floor 2 decimal places
        const floor2Decimal = (value) => {
            return Math.floor(value * 100) / 100;
        }
        response = Object.fromEntries(
            Object.entries(responseCopy).map(([key, array]) => [
                key,
                array.map(item => ({
                    ...item,
                    value: floor2Decimal(item.value)
                }))
            ])
        );
        const commonOptions = {
            dataLabels: {
                enabled: false
            },
            markers: {
                size: 0,
            },
            title: {
                text: 'Stock Price Movement',
                align: 'left'
            },
            stroke: {
                curve: 'stepline',
                width: 2
            },
            fill: {
                type: 'gradient',
                gradient: {
                    shadeIntensity: 1,
                    inverseColors: false,
                    opacityFrom: 1,
                    opacityTo: 0.8,
                    stops: [0, 90, 100]
                },
            },
            xaxis: {
                type: 'datetime',
                labels: {
                    datetimeUTC: false,  // This makes it use local time
                    format: 'dd/MM/yyyy HH:mm',
                    style: {
                        fontSize: '11px'
                    },

                },
                tooltip: {
                    formatter: function (val) {
                        return new Date(val).toLocaleString(); // Local time in tooltip
                    }
                }
            },
            tooltip: {
                enabled: true,
                followCursor: true,    // Disable follow cursor
                intersect: false,       // Show tooltip even when not directly over point
                shared: true,           // Show all series in tooltip
                custom: undefined,      // Remove any custom tooltip
                fixed: {
                    enabled: true,      // Enable fixed position
                    position: 'topLeft', // Position the tooltip
                    offsetX: 10,        // Add some horizontal offset
                    offsetY: 10,        // Add some vertical offset
                },
                x: {
                    format: 'dd MMM yyyy HH:mm'
                },
                marker: {
                    show: true,
                },
                onDatasetHover: {
                    highlightDataSeries: true,
                },
            },

        };

        return [
            {
                // Eboiler Chart
                series: [{
                    name: "Eboiler",
                    data: responseCopy["W95_BO1_input"].map(item => ({
                        x: new Date(item.datetime).getTime(),
                        y: item.value
                    }))
                }],
                options: {
                    ...commonOptions,

                    xaxis: {
                        type: "datetime",

                    },
                    title: {
                        text: '',
                        show: false
                    },

                    colors: ['#1F9562'],  // Blue
                    fill: {
                        type: 'gradient',
                        gradient: {
                            shadeIntensity: 1,
                            inverseColors: false,
                            opacityFrom: 0.5,
                            opacityTo: 0,
                            stops: [0, 90, 100]
                        },
                    },
                    yaxis: {
                        min: 0,
                        max: 1500,
                        title: {
                            text: 'Eboiler power (kW)',
                            style: {
                                fontSize: '11px',
                                fontWeight: 400,
                                fontFamily: 'Helvetica, Arial, sans-serif',

                            }
                        },
                    },

                }
            },
            {
                // W95 buffer Chart
                series: [{
                    name: "W95 buffer",
                    data: response["W95_setpoint"].map(item => ({
                        x: new Date(item.datetime).getTime(),
                        y: item.value
                    }))
                }],
                options: {
                    ...commonOptions,
                    title: {
                        text: '',
                        align: 'left'
                    },
                    colors: ['#483F5E'],  // Blue
                    fill: {
                        type: 'gradient',
                        gradient: {
                            shadeIntensity: 1,
                            inverseColors: false,
                            opacityFrom: 0.5,
                            opacityTo: 0,
                            stops: [0, 90, 100]
                        },
                    },
                    yaxis: {
                        min: 0,
                        max: 100,
                        title: {
                            text: 'W95 Tank Setpoint (%)',
                            style: {
                                fontSize: '11px',
                                fontWeight: 400,
                                fontFamily: 'Helvetica, Arial, sans-serif',

                            }
                        },
                    }
                }
            },
            {
                // W55 buffer Chart
                series: [{
                    name: "W55 buffer",
                    data: response["W55_setpoint"].map(item => ({
                        x: new Date(item.datetime).getTime(),
                        y: item.value
                    }))
                }],
                options: {
                    ...commonOptions,
                    title: {
                        text: '',
                        align: 'left'
                    },
                    colors: ['#FEB019'],  // Blue
                    fill: {
                        type: 'gradient',
                        gradient: {
                            shadeIntensity: 1,
                            inverseColors: false,
                            opacityFrom: 0.5,
                            opacityTo: 0,
                            stops: [0, 90, 100]
                        },
                    },
                    yaxis: {
                        min: 0,
                        max: 100,
                        title: {
                            text: 'W55 Tank Setpoint (%)',
                            style: {
                                fontSize: '11px',
                                fontWeight: 400,
                                fontFamily: 'Helvetica, Arial, sans-serif',

                            }
                        },
                    }
                }
            },
            {
                // W7 buffer Chart
                series: [{
                    name: "W7 buffer",
                    data: response["W7_setpoint"].map(item => ({
                        x: new Date(item.datetime).getTime(),
                        y: item.value
                    }))
                }],
                options: {
                    ...commonOptions,
                    title: {
                        text: '',
                        align: 'left'
                    },
                    colors: ['#3f7c80'],  // Blue
                    fill: {
                        type: 'gradient',
                        gradient: {
                            shadeIntensity: 1,
                            inverseColors: false,
                            opacityFrom: 0.5,
                            opacityTo: 0,
                            stops: [0, 90, 100]
                        },
                    },
                    yaxis: {
                        min: 0,
                        max: 100,
                        title: {
                            text: 'W7 Tank Setpoint (%)',
                            style: {
                                fontSize: '11px',
                                fontWeight: 400,
                                fontFamily: 'Helvetica, Arial, sans-serif',

                            }
                        },

                    }
                }
            }
        ];
    };


    const fetchSetpointsData = async () => {
        try {
            const route = `/esid-24-003/get-setpoints`;
            const response = await apiGet("UX_HTTP_API", route);
            try {
                const chartConfigs = getChartConfigs(response);
                setState(chartConfigs);
            } catch (error) {
                console.error('Error fetching data:', error);
            }
            setLoading(false);
            return response;
        } catch (error) {
            console.error('API Error details:', {
                status: error.response?.status,
                message: error.message,
                data: error.response?.data
            });
            throw error;
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        try {
            setLoading(true);
            fetchSetpointsData();
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    }, []);

    // make api call to get the data for the graphs every 10 seconds
    useEffect(() => {
        const interval = setInterval(() => {
            fetchSetpointsData();
        }, 15000);
        return () => clearInterval(interval);
    }, []);
    useEffect(() => {
        setCurrentTime(dayjs().format("DD-MM-YYYY HH:mm:ss"));
    }, [settingsPairs]);

    const findCurrentAfrrReservation = () => {
        const now = dayjs();
        if (!afrrReservations || afrrReservations.length === 0) {
            return null;
        }
        // Find all reservations that have started before now
        const pastOrCurrent = afrrReservations.filter(reservation =>
            dayjs(reservation.from).isBefore(now)
        );
        if (pastOrCurrent.length > 0) {
            // Get the latest one (by 'from' date)
            const latest = pastOrCurrent.reduce((a, b) =>
                dayjs(a.from).isAfter(dayjs(b.from)) ? a : b
            );
            return {
                value: latest.afrr_down_capacity_reserved,
                date: latest.from,
            };
        }
        // If none in the past, find the first reservation in the future
        const future = afrrReservations
            .filter(reservation => dayjs(reservation.from).isAfter(now))
            .sort((a, b) => dayjs(a.from) - dayjs(b.from));

        if (future.length > 0) {
            return {
                value: future[0].afrr_down_capacity_reserved,
                date: future[0].from,
            };
        }
        // If no reservations at all, return null
        return null;
    }
    return (
        <Box ml={"40px"} sx={{ justifyContent: "center", display: "flex" }}>
            <Box sx={{
                ml: 2,
                mr: 2,
                minHeight: screenSize.height - 35,
                width: screenSize.width,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                alignContent: "center",
                alignSelf: "center"
            }}>
                <Grid
                    container
                    spacing={2}  // Add spacing between grid items
                    justifyContent="center"  // Center horizontally
                    alignItems="center"  // Center vertically
                >
                    {state?.map((chart, index) => (
                        <Grid
                            item
                            xs={12}      // Full width on mobile
                            sm={12}      // Full width on small screens
                            md={6}       // Half width on medium screens and up
                            lg={6}       // Half width on large screens
                            xl={6}       // Half width on extra large screens
                            key={index}
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',  // Center the Box within Grid item
                                width: graphSize.width,
                                height: graphSize.height,
                            }}
                        >

                            <Box
                                sx={[
                                    styles(colors).graphBox,
                                    {
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',  // Center content within Box
                                        justifyContent: "center",
                                        width: '100%',        // Take full width of grid item
                                        height: '100%',       // Take full height of grid item
                                        maxWidth: {          // Responsive max-width
                                            xs: '100%',       // 95% of container on mobile
                                            sm: '100%',       // 95% on small screens
                                            md: '100%',       // 95% on medium screens
                                            lg: '100%',       // 95% on large screens
                                        },
                                    }
                                ]}
                            >
                                <Typography variant="h6" sx={{ textAlign: "left", mb: 1, fontWeight: "bold", width: "100%" }}>
                                    {chart.series[0].name}
                                </Typography>
                                <Box sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    alignContent: "center",
                                    alignSelf: "center",
                                    width: "100%",
                                    height: "20%",
                                    marginBottom: 1,
                                    flexWrap: "wrap"
                                }}>
                                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", alignContent: "center", alignSelf: "center", width: "100%", flexWrap: "wrap" }}>
                                        {index === 0 && <>
                                            <CustomInfoButton
                                                title={"Endustries Setpoint"}
                                                value={bufferValues?.aFRR?.spValue}
                                                subinfo={bufferValues?.aFRR?.date && bufferValues?.aFRR?.date}
                                                iconColor={colors.entras_green[200]}
                                                width={settingsPairs?.bo1_manual_override?.override ? 160 : 200}
                                                units={"kW"}
                                            />
                                            <CustomInfoButton
                                                title={"Process Value"}
                                                value={bufferValues?.aFRR?.pvValue}
                                                subinfo={bufferValues?.aFRR?.date && bufferValues?.aFRR?.date}
                                                iconColor={colors.entras_green[200]}
                                                width={settingsPairs?.bo1_manual_override?.override ? 160 : 200}
                                                units={"kW"}
                                            />
                                            <CustomInfoButtonAfrr
                                                title={"aFRR Down Reserved"}
                                                value={findCurrentAfrrReservation()?.value}
                                                subinfo={findCurrentAfrrReservation()?.date}
                                                iconColor={colors.entras_green[200]}
                                                width={settingsPairs?.bo1_manual_override?.override ? 160 : 200}
                                                units={"kW"}
                                            />
                                            {settingsPairs?.bo1_manual_override?.override && <CustomManualInfoButton
                                                title={"Setpoint Override"}
                                                value={settingsPairs?.bo1_manual_override?.setpoint_value}
                                                subinfo={currentTime}
                                                iconColor={colors.entras_green[200]}
                                                width={settingsPairs?.bo1_manual_override?.override ? 160 : 200}
                                                units={"kW"}
                                            />}
                                        </>}
                                        {
                                            index === 1 && (<>
                                                <CustomInfoButton
                                                    title={"Actual Setpoint"}
                                                    value={bufferValues?.W95?.spValue}
                                                    subinfo={bufferValues?.W95?.date && bufferValues?.W95?.date}
                                                    iconColor={colors.entras_green[200]}
                                                    width={settingsPairs?.w95_manual_override?.override ? 160 : 200}
                                                    units={"%"}
                                                />
                                                <CustomInfoButton
                                                    title={"Process Value"}
                                                    value={bufferValues?.W95?.pvValue}
                                                    subinfo={bufferValues?.W95?.date && bufferValues?.W95?.date}
                                                    iconColor={colors.entras_green[200]}
                                                    width={settingsPairs?.w95_manual_override?.override ? 160 : 200}
                                                    units={"%"}
                                                />
                                                <CustomInfoButton
                                                    title={"Endustries Setpoint"}
                                                    value={bufferValues?.W95?.esValue}
                                                    subinfo={bufferValues?.W95?.date && bufferValues?.W95?.date}
                                                    iconColor={colors.entras_green[200]}
                                                    width={settingsPairs?.w95_manual_override?.override ? 160 : 200}
                                                    units={"%"}
                                                />
                                                {settingsPairs?.w95_manual_override?.override && <CustomManualInfoButton
                                                    title={"Setpoint Override"}
                                                    value={settingsPairs?.w95_manual_override?.setpoint_value}
                                                    subinfo={currentTime}
                                                    iconColor={colors.entras_green[200]}
                                                    width={settingsPairs?.w95_manual_override?.override ? 160 : 200}
                                                    units={"%"}
                                                />}
                                            </>)
                                        }
                                        {index === 2 && <>
                                            <CustomInfoButton
                                                title={"Actual Setpoint"}
                                                value={bufferValues?.W55?.spValue}
                                                subinfo={bufferValues?.W55?.date && bufferValues?.W55?.date}
                                                iconColor={colors.entras_green[200]}
                                                width={settingsPairs?.w55_manual_override?.override ? 160 : 200}
                                                units={"%"}
                                            />
                                            <CustomInfoButton
                                                title={"Process Value"}
                                                value={bufferValues?.W55?.pvValue}
                                                subinfo={bufferValues?.W55?.date && bufferValues?.W55?.date}
                                                iconColor={colors.entras_green[200]}
                                                width={settingsPairs?.w55_manual_override?.override ? 160 : 200}
                                                units={"%"}
                                            />
                                            <CustomInfoButton
                                                title={"Endustries Setpoint"}
                                                value={bufferValues?.W55?.esValue}
                                                subinfo={bufferValues?.W55?.date && bufferValues?.W55?.date}
                                                iconColor={colors.entras_green[200]}
                                                width={settingsPairs?.w55_manual_override?.override ? 160 : 200}
                                                units={"%"}
                                            />
                                            {settingsPairs?.w55_manual_override?.override && <CustomManualInfoButton
                                                title={"Setpoint Override"}
                                                value={settingsPairs?.w55_manual_override?.setpoint_value}
                                                subinfo={currentTime}
                                                iconColor={colors.entras_green[200]}
                                                width={settingsPairs?.w55_manual_override?.override ? 160 : 200}
                                                units={"%"}
                                            />}
                                        </>}
                                        {index === 3 && <>
                                            <CustomInfoButton
                                                title={"Actual Setpoint"}
                                                value={bufferValues?.W7?.spValue}
                                                subinfo={bufferValues?.W7?.date && bufferValues?.W7?.date}
                                                iconColor={colors.entras_green[200]}
                                                width={settingsPairs?.w7_manual_override?.override ? 160 : 200}
                                                units={"%"}
                                            />
                                            <CustomInfoButton
                                                title={"Process Value"}
                                                value={bufferValues?.W7?.pvValue}
                                                subinfo={bufferValues?.W7?.date && bufferValues?.W7?.date}
                                                iconColor={colors.entras_green[200]}
                                                width={settingsPairs?.w7_manual_override?.override ? 160 : 200}
                                                units={"%"}
                                            />
                                            <CustomInfoButton
                                                title={"Endustries Setpoint"}
                                                value={bufferValues?.W7?.esValue}
                                                subinfo={bufferValues?.W7?.date && bufferValues?.W7?.date}
                                                iconColor={colors.entras_green[200]}
                                                width={settingsPairs?.w7_manual_override?.override ? 160 : 200}
                                                units={"%"}
                                            />
                                            {settingsPairs?.w7_manual_override?.override && <CustomManualInfoButton
                                                title={"Setpoint Override"}
                                                value={settingsPairs?.w7_manual_override?.setpoint_value}
                                                subinfo={currentTime}
                                                iconColor={colors.entras_green[200]}
                                                width={settingsPairs?.w7_manual_override?.override ? 160 : 200}
                                                units={"%"}
                                            />}
                                        </>}
                                    </Box>
                                </Box>
                                <Box sx={{ width: '100%', height: '80%' }}>
                                    {index === 0 && state && state[0] && <EboilerGraph state={state[0]} fullscreenGraphs={fullscreenGraphs} afrrReservations={afrrReservations} />}
                                    {index === 1 && state && state[1] && <W95Graph state={state[1]} fullscreenGraphs={fullscreenGraphs} setFullscreenGraphs={setFullscreenGraphs} />}
                                    {index === 2 && state && state[2] && <W55Graph state={state[2]} fullscreenGraphs={fullscreenGraphs} setFullscreenGraphs={setFullscreenGraphs} />}
                                    {index === 3 && state && state[3] && <W7Graph state={state[3]} fullscreenGraphs={fullscreenGraphs} setFullscreenGraphs={setFullscreenGraphs} />}

                                </Box>
                            </Box>
                        </Grid>
                    ))}

                </Grid>
                <FullScreenGraphsDialog
                    open={fullscreenGraphs}
                    onClose={() => setFullscreenGraphs(false)}
                    graphs={state} // Your chart data
                />
            </Box>

        </Box >
    );
};

export default Dashboard;

const styles = (colors) => ({
    mainContainer: {
        ml: 4,
        justifyContent: "center",
        display: "flex",
        // Scrollbar customization
        '&::-webkit-scrollbar': {
            width: '8px',
        },
        '&::-webkit-scrollbar-track': {
            backgroundColor: '#f0f0f0',
            borderRadius: '5px',
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: colors.greenAccent[500],
            borderRadius: '5px',
            '&:hover': {
                backgroundColor: colors.greenAccent[600],
            },
        },
    },
    subViewBox: { width: "32%", height: "100%", mt: 4 },
    cardBox: {
        pl: 2,
        pr: 2,
        textAlign: "center",
        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
        borderRadius: 4,
        bgcolor: "#fff",
        justifyContent: "center",
        alignItems: "center",
        alignSelf: "center",
        pb: 4,
    },
    smallBoxInfo: {
        bgcolor: "#fff",
        borderRadius: 3,
        alignItems: "center",
        justifyContent: "center",
        mb: 2,
    },
    smallBoxGraph: {
        //bgcolor: "rgba(63,124,128,0.1)",
        bgcolor: "#fff",
        borderRadius: 3,
        alignItems: "center",
        justifyContent: "center",
        //boxShadow:3,
        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
        mt: 2,
    },

    graphBox: {
        //bgcolor: "rgba(63,124,128,0.1)",
        bgcolor: "#fff",
        borderRadius: 3,
        alignItems: "center",
        justifyContent: "center",
        //boxShadow:3,
        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
        p: 2,
        pb: 4,
        /*        display: "flex",
               flexDirection: "column",
               justifyContent: "center",
               alignItems: "center",
               alignSelf: "center", */
    }
})
